import { Directive, Component, OnInit, OnDestroy, Input, ChangeDetectorRef } from "@angular/core";
import { ActivatedRoute, Params } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { BreadcrumbService } from "../../services/breadcrumb.service";

export interface ListViewOptions {
  showPaging?: boolean;
  showFilters?: boolean;
  parsePageParams?: boolean;
  renderEmpty?: boolean;
  pager?: any;
}

export interface ListViewDelegate<T> {
  parseSearchCriteria?: (params: Params, localParams: any) => string;
  performSearch(searchParams: SearchParams): Observable<T[]>;
  navigate?(params: SearchParams): void;
  export?: (searchCriteria: string) => void;
  refresh?: Observable<void>;
  updateSearch?: Observable<string>;
}

export interface SearchParams {
  search?: string;
  page?: number;
}

@Component({
  selector: "list-view",
  templateUrl: "./list-view.html",
  standalone: false,
})
export class ListView<T> implements OnInit, OnDestroy {
  items: T[];
  searchCriteria: string;
  dataLoaded: boolean;
  lastSearchFilters: SearchParams = {};
  @Input() delegate: ListViewDelegate<T>;
  @Input() options: ListViewOptions = {};
  subscriptions: Subscription[] = [];
  canExport: boolean;
  @Input() buttons: any[] = [];

  constructor(public route: ActivatedRoute, private cdr: ChangeDetectorRef) {
    this.items = [];
  }

  ngOnInit() {
    if (this.delegate.export) {
      this.buttons.push({
        style: "primary",
        icon: "save-file",
        tooltip: "Export data",
        canExecute: () => true,
        action: this.export.bind(this),
      });
    }

    if (this.options.parsePageParams !== false) {
      this.subscriptions.push(
        this.route.params.subscribe((params: Params) => {
          let localParams = BreadcrumbService.parse(this.route).pop().routeLink.pop();

          this.searchCriteria = this.delegate.parseSearchCriteria
            ? this.delegate.parseSearchCriteria(params, localParams)
            : params["search"]
            ? decodeURI(params["search"])
            : "";

          let pageIndex = +params["page"];

          this.loadItems({
            search: this.searchCriteria,
            page: isNaN(pageIndex) ? undefined : pageIndex,
          });
        })
      );
    }

    if (this.delegate.refresh) {
      this.subscriptions.push(
        this.delegate.refresh.subscribe(() => this.loadItems(this.lastSearchFilters))
      );
    }

    if (this.delegate.updateSearch) {
      this.subscriptions.push(
        this.delegate.updateSearch.subscribe((searchCriteria) =>
          this.loadItemsOrNavigate((this.searchCriteria = searchCriteria))
        )
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  pageChanged(pageNumber: number) {
    this.loadItemsOrNavigate(this.searchCriteria, pageNumber);
  }

  searchCriteriaChanged(criteria: string) {
    this.loadItemsOrNavigate(criteria);
  }

  loadItemsOrNavigate(searchCriteria: string, page?: number) {
    let searchFilters: SearchParams = { search: searchCriteria, page: page },
      hasChanged = Object.keys(searchFilters).some(
        (x) => searchFilters[x] != this.lastSearchFilters[x]
      );
    if (hasChanged && this.delegate.navigate) {
      this.selfNavigate(searchFilters);
    } else {
      this.loadItems(searchFilters);
    }
  }

  selfNavigate(searchParams: SearchParams) {
    let pageParams = {};

    // remove undefined filter params
    Object.keys(searchParams)
      .filter((x) => <any>searchParams[x])
      .forEach((x) => (pageParams[x] = <any>searchParams[x]));

    this.delegate.navigate(pageParams);
  }

  loadItems(searchParams: SearchParams) {
    this.delegate.performSearch(searchParams).subscribe((items) => {
      this.items = items;
      this.dataLoaded = true;
      this.cdr.detectChanges();
    });
    this.lastSearchFilters = searchParams;
  }

  export() {
    this.delegate.export(this.searchCriteria);
  }
}

@Directive({ selector: "list-render-items", standalone: false })
export class ListRenderItems {}

@Directive({ selector: "list-search-box", standalone: false })
export class ListSearchBox {}
